// Hybrid retrieval: BM25 ∪ semantic-embedding cosine sim, merged via
// Reciprocal Rank Fusion (RRF). RRF is the standard fusion in IR research
// because it doesn't require score calibration across spaces.
//
//   RRF score for doc d = Σ_r 1 / (k + rank(d, r))    (k=60 standard)
//
// Reference: Cormack, G. V., Clarke, C. L. A., & Buettcher, S. (2009).
//   "Reciprocal rank fusion outperforms Condorcet and individual rank
//    learning methods."
//
// Returns top-K chunk indices + the source ranks for transparency in trace.

import { FULL_CORPUS as CORPUS, corpusForBM25, type Chunk } from "@/data/corpus";
import { buildIndex, search as bm25Search, type BM25Hit } from "@/lib/bm25";
import { embed, embedMany, cosine, getEmbedderState } from "@/lib/embedder";

const RRF_K = 60;

const BM25_INDEX = buildIndex(corpusForBM25());

let corpusEmbeddings: Float32Array[] | null = null;
let corpusEmbeddingsPromise: Promise<Float32Array[]> | null = null;

async function getCorpusEmbeddings(): Promise<Float32Array[]> {
  if (corpusEmbeddings) return corpusEmbeddings;
  if (!corpusEmbeddingsPromise) {
    corpusEmbeddingsPromise = (async () => {
      const texts = CORPUS.map((c) => c.text);
      const embs = await embedMany(texts);
      corpusEmbeddings = embs;
      return embs;
    })();
  }
  return corpusEmbeddingsPromise;
}

// Public: trigger corpus embedding precompute in background.
export function preloadCorpusEmbeddings() {
  void getCorpusEmbeddings().catch(() => {});
}

export type RetrievalTrace = {
  query: string;
  bm25Hits: { id: string; score: number }[];
  semanticHits: { id: string; score: number }[];
  finalHits: { id: string; rrfScore: number; sources: ("bm25" | "semantic")[] }[];
  timings: { bm25Ms: number; embedMs: number; semanticMs: number; fuseMs: number; totalMs: number };
  embedderUsed: boolean;
};

export type RetrievalResult = {
  chunks: Chunk[];
  chunkEmbeddings: Float32Array[]; // aligned with chunks; empty if embedder didn't run
  trace: RetrievalTrace;
};

export async function retrieve(
  query: string,
  topK = 5,
): Promise<RetrievalResult> {
  const t0 = performance.now();

  // ── BM25 (always, synchronous, fast) ─────────────────────────
  const bm25Start = performance.now();
  const bm25Top = bm25Search(BM25_INDEX, query, 20);
  const bm25Ms = performance.now() - bm25Start;

  // ── Semantic (skip if embedder not ready — graceful degrade) ─
  let semanticTop: { docIdx: number; id: string; score: number }[] = [];
  let embedMs = 0;
  let semanticMs = 0;
  let embedderUsed = false;
  const state = getEmbedderState();
  if (state === "ready" || corpusEmbeddings) {
    try {
      const embedStart = performance.now();
      const qEmb = await embed(query);
      embedMs = performance.now() - embedStart;
      const semStart = performance.now();
      const corpEmbs = await getCorpusEmbeddings();
      const scored = corpEmbs.map((emb, idx) => ({
        docIdx: idx,
        id: CORPUS[idx].id,
        score: cosine(qEmb, emb),
      }));
      scored.sort((a, b) => b.score - a.score);
      semanticTop = scored.slice(0, 20);
      semanticMs = performance.now() - semStart;
      embedderUsed = true;
    } catch {
      // graceful degrade — BM25 alone is still very good
    }
  }

  // ── RRF fuse ─────────────────────────────────────────────────
  const fuseStart = performance.now();
  const rrf: Map<number, { score: number; sources: ("bm25" | "semantic")[] }> = new Map();
  bm25Top.forEach((h, rank) => {
    const r = 1 / (RRF_K + rank + 1);
    const cur = rrf.get(h.docIdx) ?? { score: 0, sources: [] };
    cur.score += r;
    cur.sources.push("bm25");
    rrf.set(h.docIdx, cur);
  });
  semanticTop.forEach((h, rank) => {
    const r = 1 / (RRF_K + rank + 1);
    const cur = rrf.get(h.docIdx) ?? { score: 0, sources: [] };
    cur.score += r;
    cur.sources.push("semantic");
    rrf.set(h.docIdx, cur);
  });
  const fused = Array.from(rrf.entries())
    .map(([docIdx, v]) => ({ docIdx, rrfScore: v.score, sources: v.sources }))
    .sort((a, b) => b.rrfScore - a.rrfScore)
    .slice(0, topK);
  const fuseMs = performance.now() - fuseStart;

  const chunks = fused.map((f) => CORPUS[f.docIdx]);
  // Map the corpus embeddings of the final chunks (when available)
  const chunkEmbeddings: Float32Array[] = corpusEmbeddings
    ? fused.map((f) => corpusEmbeddings![f.docIdx])
    : [];
  const totalMs = performance.now() - t0;

  return {
    chunks,
    chunkEmbeddings,
    trace: {
      query,
      bm25Hits: bm25Top.map((h) => ({ id: h.id, score: round(h.score) })),
      semanticHits: semanticTop.map((h) => ({ id: h.id, score: round(h.score) })),
      finalHits: fused.map((f) => ({
        id: CORPUS[f.docIdx].id,
        rrfScore: round(f.rrfScore),
        sources: f.sources,
      })),
      timings: {
        bm25Ms: round(bm25Ms),
        embedMs: round(embedMs),
        semanticMs: round(semanticMs),
        fuseMs: round(fuseMs),
        totalMs: round(totalMs),
      },
      embedderUsed,
    },
  };
}

// Re-export BM25 hits type for callers
export type { BM25Hit };

function round(n: number) {
  return Math.round(n * 1000) / 1000;
}
